import { useCallback, useEffect, useMemo, useState } from "react";
import { FiArrowLeft, FiSearch, FiX } from "react-icons/fi";
import bankService from "../../services/bank.service";
import "../../styles/users.css";
import "../../styles/bankDetails.css";

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
}

function formatUploadStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "Completed";
  if (s === "failed") return "Failed";
  if (s === "processing") return "Processing";
  if (s === "pending") return "Pending";
  return status || "—";
}

function formatUploaderRole(role) {
  const r = String(role || "").toUpperCase();
  if (r === "BANK_PERSON") return "Bank / NBFC Agent";
  if (r === "BANK_ADMIN") return "Bank / NBFC Admin";
  return "";
}

function uploadStatusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "br-status br-status--ok";
  if (s === "failed") return "br-status br-status--bad";
  if (s === "processing" || s === "pending") return "br-status br-status--warn";
  return "br-status";
}

function bankStatusClass(status) {
  const s = String(status || "active").toLowerCase();
  if (s === "active") return "br-status br-status--ok";
  if (s === "inactive" || s === "disabled") return "br-status br-status--bad";
  return "br-status";
}

function formatRecordCount(batch) {
  const total = Number(batch?.totalRows);
  if (Number.isFinite(total) && total > 0) return total.toLocaleString("en-IN");
  const success = Number(batch?.successRows);
  if (Number.isFinite(success) && success > 0) return success.toLocaleString("en-IN");
  return (Number.isFinite(total) ? total : 0).toLocaleString("en-IN");
}

function SearchBar({ value, onChange, onSubmit, onClear, placeholder, label }) {
  return (
    <form
      className="br-connected__search"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="br-connected__search-field">
        <FiSearch className="br-connected__search-icon" aria-hidden="true" />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={label}
        />
        {value ? (
          <button
            type="button"
            className="br-connected__search-clear"
            aria-label="Clear search"
            onClick={onClear}
          >
            <FiX />
          </button>
        ) : null}
      </div>
      <button type="submit" className="secondary-page-btn br-connected__search-btn">
        <FiSearch /> Search
      </button>
    </form>
  );
}

/**
 * Connected Banks → Upload History → Upload Details (in-page, no vehicle-row scan).
 */
export default function ConnectedBanksSection() {
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(true);
  const [banksError, setBanksError] = useState("");
  const [bankSearch, setBankSearch] = useState("");

  const [selectedBank, setSelectedBank] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchPage, setBatchPage] = useState(1);
  const [batchSearch, setBatchSearch] = useState("");
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchesError, setBatchesError] = useState("");

  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetailLoading, setBatchDetailLoading] = useState(false);
  const [batchDetailError, setBatchDetailError] = useState("");

  const BATCH_LIMIT = 20;

  const loadBanks = useCallback(async (search = "") => {
    setBanksLoading(true);
    setBanksError("");
    try {
      const res = await bankService.getConnectedBanks({
        search: search || undefined,
      });
      setBanks(res?.data?.data?.banks || []);
    } catch (err) {
      setBanks([]);
      setBanksError(
        err?.response?.data?.message || "Unable to load connected banks / NBFCs."
      );
    } finally {
      setBanksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBanks("");
  }, [loadBanks]);

  const loadUploads = useCallback(async (bank, page = 1, search = "") => {
    if (!bank?.bankId) return;
    setBatchesLoading(true);
    setBatchesError("");
    try {
      const res = await bankService.getRepoBankUploads({
        bankId: bank.bankId,
        page,
        limit: BATCH_LIMIT,
        search: search || undefined,
      });
      const data = res?.data?.data || {};
      setBatches(data.batches || []);
      setBatchTotal(Number(data.total) || 0);
      if (data.bank) {
        setSelectedBank((prev) => ({ ...(prev || bank), ...data.bank }));
      }
    } catch (err) {
      setBatches([]);
      setBatchTotal(0);
      setBatchesError(
        err?.response?.data?.message || "Unable to load upload history."
      );
    } finally {
      setBatchesLoading(false);
    }
  }, []);

  const openBank = (bank) => {
    setSelectedBank(bank);
    setSelectedBatch(null);
    setBatchPage(1);
    setBatchSearch("");
    loadUploads(bank, 1, "");
  };

  const backToBanks = () => {
    setSelectedBank(null);
    setSelectedBatch(null);
    setBatches([]);
    setBatchTotal(0);
    setBatchesError("");
    setBatchDetailError("");
  };

  const openBatch = async (batch) => {
    setBatchDetailLoading(true);
    setBatchDetailError("");
    setSelectedBatch(null);
    try {
      const res = await bankService.getRepoBankUpload(batch._id || batch.batchId);
      setSelectedBatch(res?.data?.data?.batch || batch);
    } catch (err) {
      setBatchDetailError(
        err?.response?.data?.message || "Unable to load upload details."
      );
      setSelectedBatch(batch);
    } finally {
      setBatchDetailLoading(false);
    }
  };

  const filteredBanks = useMemo(() => {
    const q = bankSearch.trim().toLowerCase();
    if (!q) return banks;
    return banks.filter(
      (b) =>
        String(b.bankName || "").toLowerCase().includes(q) ||
        String(b.bankCode || "").toLowerCase().includes(q) ||
        String(b.phone || "").toLowerCase().includes(q) ||
        String(b.bankerPhone || "").toLowerCase().includes(q) ||
        String(b.name || "").toLowerCase().includes(q)
    );
  }, [banks, bankSearch]);

  const batchPages = Math.max(1, Math.ceil(batchTotal / BATCH_LIMIT));

  if (selectedBank) {
    const bankLabel = `${selectedBank.bankName || "Bank / NBFC"}${
      selectedBank.bankCode ? ` (${selectedBank.bankCode})` : ""
    }`;
    const bankPhone = selectedBank.phone || selectedBank.bankerPhone || "";

    return (
      <section className="br-connected" aria-label="Bank / NBFC upload history">
        <div className="br-connected__toolbar">
          <button type="button" className="secondary-page-btn" onClick={backToBanks}>
            <FiArrowLeft /> Back to Connected Banks / NBFCs
          </button>
          <div className="br-connected__title-wrap">
            <h3 className="br-connected__title" title={bankLabel}>
              {bankLabel}
            </h3>
            <p className="muted br-connected__subtitle">
              Upload history{bankPhone ? ` · ${bankPhone}` : ""}
            </p>
          </div>
        </div>

        <SearchBar
          value={batchSearch}
          onChange={setBatchSearch}
          placeholder="Search file name…"
          label="Search upload history"
          onSubmit={() => {
            setBatchPage(1);
            loadUploads(selectedBank, 1, batchSearch);
          }}
          onClear={() => {
            setBatchSearch("");
            setBatchPage(1);
            loadUploads(selectedBank, 1, "");
          }}
        />

        {batchesError ? <p className="error-text">{batchesError}</p> : null}

        {!batchesLoading ? (
          <p className="muted br-connected__count">
            {batchTotal.toLocaleString("en-IN")} {batchTotal === 1 ? "Upload" : "Uploads"}
          </p>
        ) : null}

        {batchesLoading ? (
          <p className="muted">Loading upload history…</p>
        ) : batches.length === 0 ? (
          <div className="br-connected__empty">No upload history found for this bank / NBFC.</div>
        ) : (
          <div className="company-table-wrap bd-excel-sheet br-excel-sheet">
            <table className="users-table excel-grid-table bd-excel-table">
              <thead>
                <tr>
                  <th>File / Batch</th>
                  <th>Uploaded By</th>
                  <th>Phone</th>
                  <th>Mail ID</th>
                  <th>Date</th>
                  <th className="br-connected__num">Total Records</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => {
                  const role = formatUploaderRole(b.uploadedBy?.role);
                  const uploader = b.uploadedBy?.name || "—";
                  return (
                    <tr
                      key={String(b._id)}
                      className="br-connected__row--clickable"
                      onClick={() => openBatch(b)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openBatch(b);
                        }
                      }}
                      tabIndex={0}
                    >
                      <td>
                        <div
                          className="br-connected__filecell"
                          title={`${b.fileName || "—"} / Batch #${b._id}`}
                        >
                          <span className="br-connected__file">{b.fileName || "—"}</span>
                          <span className="br-connected__batch"> / Batch #{b._id}</span>
                        </div>
                      </td>
                      <td>
                        <div className="br-connected__uploader" title={role ? `${uploader} — ${role}` : uploader}>
                          <span>{uploader}</span>
                          {role ? <span className="br-connected__role"> — {role}</span> : null}
                        </div>
                      </td>
                      <td className="br-connected__nowrap">{b.uploadedBy?.phone || "—"}</td>
                      <td className="br-connected__nowrap">{b.uploadedBy?.email || "—"}</td>
                      <td className="br-connected__nowrap">{formatDateTime(b.createdAt)}</td>
                      <td className="br-connected__num">{formatRecordCount(b)}</td>
                      <td>
                        <span className={uploadStatusClass(b.status)}>
                          {formatUploadStatus(b.status)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {batchPages > 1 && (
          <div className="br-connected__pager">
            <button
              type="button"
              className="secondary-page-btn"
              disabled={batchPage <= 1 || batchesLoading}
              onClick={() => {
                const p = batchPage - 1;
                setBatchPage(p);
                loadUploads(selectedBank, p, batchSearch);
              }}
            >
              Prev
            </button>
            <span className="muted">
              Page {batchPage} of {batchPages}
            </span>
            <button
              type="button"
              className="secondary-page-btn"
              disabled={batchPage >= batchPages || batchesLoading}
              onClick={() => {
                const p = batchPage + 1;
                setBatchPage(p);
                loadUploads(selectedBank, p, batchSearch);
              }}
            >
              Next
            </button>
          </div>
        )}

        {(selectedBatch || batchDetailLoading || batchDetailError) && (
          <div className="br-upload-detail" role="region" aria-label="Upload details">
            <div className="br-upload-detail__head">
              <h4>Upload Details</h4>
              <button
                type="button"
                className="secondary-page-btn"
                onClick={() => {
                  setSelectedBatch(null);
                  setBatchDetailError("");
                }}
              >
                Close
              </button>
            </div>
            {batchDetailLoading ? (
              <p className="muted">Loading details…</p>
            ) : (
              <>
                {batchDetailError ? <p className="error-text">{batchDetailError}</p> : null}
                {selectedBatch ? (
                  <div className="company-table-wrap bd-excel-sheet br-excel-sheet">
                    <table className="users-table excel-grid-table bd-excel-table">
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Bank / NBFC Name</td>
                          <td>
                            {selectedBatch.bankName || selectedBank.bankName || "—"}
                            {selectedBatch.bankCode || selectedBank.bankCode
                              ? ` (${selectedBatch.bankCode || selectedBank.bankCode})`
                              : ""}
                          </td>
                        </tr>
                        <tr>
                          <td>Upload Date &amp; Time</td>
                          <td>{formatDateTime(selectedBatch.createdAt)}</td>
                        </tr>
                        <tr>
                          <td>Completion Time</td>
                          <td>{formatDateTime(selectedBatch.updatedAt)}</td>
                        </tr>
                        <tr>
                          <td>Uploaded By</td>
                          <td>
                            {selectedBatch.uploadedBy?.name || "—"}
                            {formatUploaderRole(selectedBatch.uploadedBy?.role)
                              ? ` — ${formatUploaderRole(selectedBatch.uploadedBy.role)}`
                              : ""}
                          </td>
                        </tr>
                        <tr>
                          <td>Phone</td>
                          <td>{selectedBatch.uploadedBy?.phone || "—"}</td>
                        </tr>
                        <tr>
                          <td>Mail ID</td>
                          <td>{selectedBatch.uploadedBy?.email || "—"}</td>
                        </tr>
                        <tr>
                          <td>Original File Name</td>
                          <td>{selectedBatch.fileName || "—"}</td>
                        </tr>
                        <tr>
                          <td>Upload Batch ID</td>
                          <td>{selectedBatch._id || selectedBatch.batchId || "—"}</td>
                        </tr>
                        <tr>
                          <td>Total Records</td>
                          <td>{Number(selectedBatch.totalRows || 0).toLocaleString("en-IN")}</td>
                        </tr>
                        <tr>
                          <td>Successfully Processed</td>
                          <td>{Number(selectedBatch.successRows || 0).toLocaleString("en-IN")}</td>
                        </tr>
                        <tr>
                          <td>Failed Records</td>
                          <td>{Number(selectedBatch.failedRows || 0).toLocaleString("en-IN")}</td>
                        </tr>
                        <tr>
                          <td>Duplicates</td>
                          <td>{Number(selectedBatch.duplicateRows || 0).toLocaleString("en-IN")}</td>
                        </tr>
                        <tr>
                          <td>Processing Status</td>
                          <td>
                            <span className={uploadStatusClass(selectedBatch.status)}>
                              {formatUploadStatus(selectedBatch.status)}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>Error Information</td>
                          <td>{selectedBatch.errorMessage || "—"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="br-connected" aria-label="Connected banks">
      <div className="br-connected__header">
        <h3 className="br-connected__title">Connected Banks / NBFCs</h3>
        <p className="muted br-connected__subtitle">
          Banks / NBFCs linked to your agency. Open a bank / NBFC to view its upload history.
        </p>
      </div>

      <SearchBar
        value={bankSearch}
        onChange={setBankSearch}
        placeholder="Search banks / NBFCs…"
        label="Search connected banks / NBFCs"
        onSubmit={() => loadBanks(bankSearch)}
        onClear={() => {
          setBankSearch("");
          loadBanks("");
        }}
      />

      {banksError ? <p className="error-text">{banksError}</p> : null}

      {!banksLoading ? (
        <p className="muted br-connected__count">
          {filteredBanks.length.toLocaleString("en-IN")}{" "}
          {filteredBanks.length === 1 ? "Bank / NBFC" : "Banks / NBFCs"}
        </p>
      ) : null}

      {banksLoading ? (
        <p className="muted">Loading connected banks / NBFCs…</p>
      ) : filteredBanks.length === 0 ? (
        <div className="br-connected__empty">No connected banks / NBFCs found.</div>
      ) : (
        <div className="company-table-wrap bd-excel-sheet br-excel-sheet">
          <table className="users-table excel-grid-table bd-excel-table">
            <thead>
              <tr>
                <th>Bank / NBFC Name</th>
                <th>Phone</th>
                <th>Mail ID</th>
                <th>Linked Date &amp; Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredBanks.map((b) => {
                const name = b.bankName || "Bank / NBFC";
                const label = b.bankCode ? `${name} (${b.bankCode})` : name;
                return (
                  <tr
                    key={String(b.bankId)}
                    className="br-connected__row--clickable"
                    onClick={() => openBank(b)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openBank(b);
                      }
                    }}
                    tabIndex={0}
                  >
                    <td>
                      <div className="br-connected__name" title={label}>
                        <span className="br-connected__name-text">{name}</span>
                        {b.bankCode ? (
                          <span className="br-connected__code"> ({b.bankCode})</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="br-connected__nowrap">{b.phone || b.bankerPhone || "—"}</td>
                    <td className="br-connected__nowrap">{b.email || "—"}</td>
                    <td className="br-connected__nowrap">{formatDateTime(b.linkedAt)}</td>
                    <td>
                      <span className={bankStatusClass(b.status || "Active")}>
                        {b.status || "Active"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
